import { Alert, Box, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { useState, useEffect } from 'react';
import type { Category } from '../../../types/domain';
import { IndividualEntriesTab } from './IndividualEntriesTab';

interface Props {
  categories: Category[];
}

/**
 * Wrapper around IndividualEntriesTab that provides per-category selection.
 *
 * NOTE: At this point Category does not yet expose a `target` field on the
 * backend DTO. Once added (Phase 2 of the startovka-100m-okres-opava plan),
 * filter `categories` by `target === 'INDIVIDUAL'` here.
 */
export function CompetitionIndividualEntriesTab({ categories }: Props) {
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(
    categories[0]?.id ?? null,
  );

  useEffect(() => {
    if (selectedCategoryId == null && categories[0]) {
      setSelectedCategoryId(categories[0].id);
    }
    if (
      selectedCategoryId != null &&
      !categories.some((c) => c.id === selectedCategoryId)
    ) {
      setSelectedCategoryId(categories[0]?.id ?? null);
    }
  }, [categories, selectedCategoryId]);

  if (categories.length === 0) {
    return (
      <Alert severity="info">
        Nejprve vytvořte alespoň jednu kategorii v záložce „Kategorie a disciplíny".
      </Alert>
    );
  }

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        sx={{ mb: 2 }}
      >
        <Typography variant="body2" color="text.secondary">
          Kategorie:
        </Typography>
        <TextField
          select
          size="small"
          value={selectedCategoryId ?? ''}
          onChange={(e) => setSelectedCategoryId(Number(e.target.value))}
          sx={{ minWidth: 240 }}
        >
          {categories.map((c) => (
            <MenuItem key={c.id} value={c.id}>
              {c.name}
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      {selectedCategoryId != null && (
        <IndividualEntriesTab key={selectedCategoryId} categoryId={selectedCategoryId} />
      )}
    </Box>
  );
}
